/**
 * "Select from imported data" picker for the Propose-correction drawer.
 *
 * Two modes:
 *  - **Daily series**: pick a source from the catalog, then a day from its
 *    `{day, value}` readings for the batch window (`GET /data/series` — needs the
 *    DUT/VPN link; degrades to a clear error + retry when unreachable).
 *  - **Uploads**: pick an accepted/parsed upload for the period, then a row + the
 *    value column (`GET /uploads/{id}/rows` — no DUT dependency).
 *
 * On a pick it hands a `PickedDatum` back to the form, which fills
 * `source_identifier`, `period_start`, and `before_value`. Picked values are the
 * backend's canonical decimal strings, passed through untouched (locked #6).
 */
import { isApiError } from "@compliance/domain";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconDatabase, IconFileText, IconRefresh } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import {
  useBatchCorrections,
  useDataSeries,
  useDataSources,
  useUploadRows,
  useUploads,
} from "../../api/hooks";
import type { BatchDetail, ManualUpload } from "../../api/types";
import {
  buildOverlayIndex,
  overlaySeriesKey,
  resolveCellDisplay,
} from "../../components/data/correctionOverlay";
import { roleDisplayName } from "../../components/data/selection";
import { formatBucketLabel, formatCell } from "../../components/data/series-data";
import { addDaysIso, enumerateDays, isoToGasDay } from "../../lib/dateRange";
import {
  type PickedDatum,
  deriveColumns,
  detectDateColumn,
  detectNumericColumns,
  pickFromMissingDay,
  pickFromSeries,
  pickFromUploadRow,
  withCorrectionContext,
} from "./correctionPick";

export function CorrectionValuePicker({
  opened,
  batch,
  onPick,
  onClose,
}: {
  opened: boolean;
  batch: BatchDetail;
  onPick: (datum: PickedDatum) => void;
  onClose: () => void;
}) {
  return (
    <Modal opened={opened} onClose={onClose} title="Select from imported data" size="lg" centered>
      <Tabs defaultValue="series" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="series" leftSection={<IconDatabase size={14} />}>
            Daily series
          </Tabs.Tab>
          <Tabs.Tab value="uploads" leftSection={<IconFileText size={14} />}>
            Uploads
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="series">
          <SeriesPick batch={batch} onPick={onPick} />
        </Tabs.Panel>
        <Tabs.Panel value="uploads">
          <UploadsPick batch={batch} onPick={onPick} />
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}

// ── Daily-series mode ───────────────────────────────────────────────────────

function SeriesPick({ batch, onPick }: { batch: BatchDetail; onPick: (d: PickedDatum) => void }) {
  const catalogQ = useDataSources();
  // Existing corrections, overlaid on the readings so a re-corrected datum is
  // picked at its EFFECTIVE value (correcting a correction), not the stale db
  // reading — and so a duplicate proposal is visible before it's made.
  const correctionsQ = useBatchCorrections(batch.batch_id);
  // Encoded as "role::facility" (facility empty for hub-scoped roles).
  const [sel, setSel] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<"daily" | "hourly">("daily");

  const options = useMemo(() => {
    const cat = catalogQ.data;
    if (!cat) return [];
    const out: { value: string; label: string }[] = [];
    for (const src of cat.sources) {
      if (src.requires_facility) {
        for (const f of cat.facilities.filter((fac) => fac.scope === src.scope)) {
          out.push({
            value: `${src.role}::${f.identifier}`,
            label: `${roleDisplayName(src.role)} · ${f.display_name}`,
          });
        }
      } else {
        out.push({ value: `${src.role}::`, label: roleDisplayName(src.role) });
      }
    }
    return out;
  }, [catalogQ.data]);

  const role = sel ? (sel.split("::")[0] ?? "") : "";
  const facility = sel ? sel.split("::")[1] || undefined : undefined;
  // Hourly is offered only for raw-totalizer roles; others stay daily.
  const hourlyCapable = useMemo(
    () => (catalogQ.data?.sources ?? []).some((s) => s.role === role && s.hourly_capable),
    [catalogQ.data, role],
  );
  const gran = hourlyCapable ? granularity : "daily";
  const range = useMemo(
    () => ({
      start: isoToGasDay(batch.period_start),
      end: addDaysIso(isoToGasDay(batch.period_end), -1),
    }),
    [batch.period_start, batch.period_end],
  );
  const seriesQ = useDataSeries(
    sel ? { role, facility, start: range.start, end: range.end, granularity: gran } : null,
  );

  // This series' correction overlay, keyed to match the row keys below.
  const overlay = useMemo(() => {
    const corrections = correctionsQ.data ?? [];
    const series = seriesQ.data;
    if (!series || corrections.length === 0) return undefined;
    const key = overlaySeriesKey(series.facility_identifier, series.role);
    const bucketKeys =
      gran === "hourly" ? new Map([[key, series.points.map((p) => p.bucket_start)]]) : undefined;
    return buildOverlayIndex(corrections, gran, bucketKeys).bySeries.get(key);
  }, [correctionsQ.data, seriesQ.data, gran]);

  let body: React.ReactNode;
  if (!sel) {
    body = (
      <Text size="sm" c="dimmed">
        Pick a source to list its daily readings for {range.start} → {range.end}.
      </Text>
    );
  } else if (seriesQ.isPending) {
    body = (
      <Group justify="center" py="lg" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading readings…
        </Text>
      </Group>
    );
  } else if (seriesQ.isError) {
    body = (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Stack gap="xs">
          <Text size="sm">
            {isApiError(seriesQ.error)
              ? `${seriesQ.error.code}: ${seriesQ.error.message}`
              : "Couldn't load readings — the DUT source may be unreachable (VPN). The Uploads tab works without it."}
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={() => seriesQ.refetch()}
          >
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  } else {
    const series = seriesQ.data;
    type Row = {
      key: string;
      label: string;
      value: string;
      unit: string;
      missing: boolean;
      marker: "approved" | "pending" | null;
      excluded: boolean;
      tooltip: string | null;
      pick: () => void;
    };
    let rows: Row[] = [];
    if (series && gran === "hourly") {
      rows = series.points.map((p) => {
        const cell = overlay?.cells.get(p.bucket_start);
        const display = resolveCellDisplay(p.value, cell, "all");
        return {
          key: p.bucket_start,
          label: formatBucketLabel(p.bucket_start, gran),
          value: formatCell(display.value, 2),
          unit: series.unit,
          missing: false,
          marker: display.marker,
          excluded: display.excluded,
          tooltip: display.tooltip,
          // Correcting a correction: the pick carries the EFFECTIVE value +
          // context so the drawer's before_value is the corrected figure.
          pick: () => onPick(withCorrectionContext(pickFromSeries(series, p), cell)),
        };
      });
    } else if (series) {
      // Daily: enumerate every gas-day in the batch window so a day with no
      // reading still renders (as a selectable 0) — the series omits empty
      // gas-days, so a truly-missing day would otherwise be un-substitutable.
      const byDay = new Map(series.points.map((p) => [p.bucket_start.slice(0, 10), p]));
      rows = enumerateDays(range.start, range.end).map((day) => {
        const point = byDay.get(day);
        const cell = overlay?.cells.get(day);
        const display = resolveCellDisplay(point?.value ?? null, cell, "all");
        const datum = point ? pickFromSeries(series, point) : pickFromMissingDay(series, day);
        // A missing day covered by an approved substitution shows the
        // effective after_value (red), not the synthetic "0".
        const value =
          display.marker != null && display.value != null
            ? formatCell(display.value, 2)
            : point
              ? formatCell(point.value, 2)
              : "0";
        return {
          key: day,
          label: day,
          value,
          unit: point ? series.unit : `${series.unit} · no data`,
          missing: !point,
          marker: display.marker,
          excluded: display.excluded,
          tooltip: display.tooltip,
          pick: () => onPick(withCorrectionContext(datum, cell)),
        };
      });
    }
    body =
      rows.length > 0 ? (
        <ScrollArea.Autosize mah={360}>
          <Table fz="sm" stickyHeader highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{gran === "hourly" ? "Hour (PT)" : "Day (PT)"}</Table.Th>
                <Table.Th ta="right">Value</Table.Th>
                <Table.Th>Unit</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr
                  key={r.key}
                  style={{ cursor: "pointer" }}
                  onClick={r.pick}
                  bg={
                    r.marker === "approved"
                      ? "red.0"
                      : r.marker === "pending"
                        ? "yellow.0"
                        : undefined
                  }
                  title={r.tooltip ?? undefined}
                  data-corrected={r.marker ?? undefined}
                >
                  <Table.Td ff="monospace">
                    <Group gap={6} wrap="nowrap">
                      {r.label}
                      {r.marker === "approved" ? (
                        <Badge color="red" size="xs" variant="light">
                          {r.excluded ? "excluded" : "corrected"}
                        </Badge>
                      ) : r.marker === "pending" ? (
                        <Badge color="yellow" size="xs" variant="light">
                          proposed
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td
                    ff="monospace"
                    ta="right"
                    c={
                      r.marker === "approved"
                        ? "red.9"
                        : r.marker === "pending"
                          ? "yellow.9"
                          : r.missing
                            ? "dimmed"
                            : undefined
                    }
                    td={r.excluded ? "line-through" : undefined}
                  >
                    {r.value}
                  </Table.Td>
                  <Table.Td c="dimmed">{r.unit}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      ) : (
        <Text size="sm" c="dimmed">
          No readings for this source in the batch period.
        </Text>
      );
  }

  return (
    <Stack gap="md">
      <Select
        label="Source"
        placeholder={catalogQ.isPending ? "Loading sources…" : "Pick a source"}
        data={options}
        value={sel}
        onChange={setSel}
        searchable
        disabled={catalogQ.isPending}
        nothingFoundMessage="No sources"
      />
      {sel && hourlyCapable ? (
        <Group justify="flex-end">
          <SegmentedControl
            size="xs"
            value={gran}
            onChange={(v) => setGranularity(v as "daily" | "hourly")}
            data={[
              { value: "daily", label: "Daily" },
              { value: "hourly", label: "Hourly" },
            ]}
          />
        </Group>
      ) : null}
      {body}
    </Stack>
  );
}

// ── Uploads mode ────────────────────────────────────────────────────────────

function UploadsPick({ batch, onPick }: { batch: BatchDetail; onPick: (d: PickedDatum) => void }) {
  const period = batch.period_start.slice(0, 7);
  const uploadsQ = useUploads(period);
  const uploads = useMemo<ManualUpload[]>(
    () =>
      (uploadsQ.data?.items ?? []).filter(
        (u) => u.batch_id === batch.batch_id || u.status === "parsed" || u.status === "accepted",
      ),
    [uploadsQ.data, batch.batch_id],
  );
  const [uploadId, setUploadId] = useState<string | null>(null);
  const selected = uploads.find((u) => u.id === uploadId) ?? null;

  return (
    <Stack gap="md">
      <Select
        label="Upload"
        placeholder={
          uploadsQ.isPending
            ? "Loading uploads…"
            : uploads.length
              ? "Pick an upload"
              : "No accepted or parse-clean uploads for this period"
        }
        data={uploads.map((u) => ({
          value: u.id,
          label: `${u.upload_type} · ${u.original_filename} · ${u.status}`,
        }))}
        value={uploadId}
        onChange={setUploadId}
        searchable
        disabled={uploadsQ.isPending || uploads.length === 0}
      />
      {selected ? (
        <UploadRowPick key={selected.id} upload={selected} onPick={onPick} />
      ) : (
        <Text size="sm" c="dimmed">
          Pick an upload to list its rows.
        </Text>
      )}
    </Stack>
  );
}

function UploadRowPick({
  upload,
  onPick,
}: {
  upload: ManualUpload;
  onPick: (d: PickedDatum) => void;
}) {
  const rowsQ = useUploadRows(upload.id, { limit: 200, offset: 0 });
  const rows = useMemo(() => rowsQ.data?.rows ?? [], [rowsQ.data]);
  const columns = useMemo(() => deriveColumns(rows), [rows]);
  const dateColumn = useMemo(() => detectDateColumn(rows, columns), [rows, columns]);
  const numericColumns = useMemo(
    () => detectNumericColumns(rows, columns, dateColumn),
    [rows, columns, dateColumn],
  );
  const [valueColumn, setValueColumn] = useState<string | null>(null);
  const effectiveCol = valueColumn ?? numericColumns[0] ?? null;

  if (rowsQ.isPending) {
    return (
      <Group justify="center" py="lg">
        <Loader size="sm" />
      </Group>
    );
  }
  if (rowsQ.isError) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Text size="sm">
          {isApiError(rowsQ.error) ? rowsQ.error.message : "Failed to load rows."}
        </Text>
      </Alert>
    );
  }
  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No parsed rows in this upload.
      </Text>
    );
  }
  if (numericColumns.length === 0 || !effectiveCol) {
    return (
      <Text size="sm" c="dimmed">
        This upload has no numeric value column to select.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Select
        label="Value column"
        data={numericColumns}
        value={effectiveCol}
        onChange={setValueColumn}
        allowDeselect={false}
      />
      <Text size="xs" c="dimmed">
        Click a row to use its{" "}
        <Text span ff="monospace">
          {effectiveCol}
        </Text>{" "}
        value.
      </Text>
      <ScrollArea.Autosize mah={320}>
        <Table fz="xs" stickyHeader highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>#</Table.Th>
              {columns.map((c) => (
                <Table.Th key={c}>{c}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr
                key={r.row_number}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  onPick(pickFromUploadRow(upload.upload_type, r, effectiveCol, dateColumn))
                }
              >
                <Table.Td ff="monospace" c="dimmed">
                  {r.row_number}
                </Table.Td>
                {columns.map((c) => (
                  <Table.Td key={c} ff="monospace" fw={c === effectiveCol ? 700 : undefined}>
                    {renderCell(r.data[c])}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    </Stack>
  );
}

function renderCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
